import type { ComponentType } from 'react'
import { ArrowUpRight, ChevronRight } from 'lucide-react'
import { Shell, useShellMeta } from '../admin/Shell.js'
import { Link } from '../router.js'
import { usePermissions } from '../admin/primitives.js'
import { SITE_FAMILIES, MODULE_STATE_LABELS, type NavFamily, type NavEntry } from './site-navigation.js'
import type { ShellActive } from '../admin/Shell.js'
import { SyntheseSignalements } from '../technique/SyntheseSignalements.js'
import { SyntheseAssociations } from '../associations/SyntheseAssociations.js'
import { SyntheseInterphone } from '../interphone/SyntheseInterphone.js'
import { useMenuLien } from '../layout/menu-tuiles.js'

/**
 * HUB DE FAMILLE (IA V2) — la page d'accueil d'une famille, générée depuis
 * le registre central : hero + tuiles premium des sous-destinations.
 *
 * Les tuiles disent la vérité (§76-78) : une destination DISPONIBLE
 * navigue ; NON CONFIGURÉ navigue vers son shell et propose de configurer ;
 * À VENIR n'ouvre rien et n'affiche AUCUN faux CTA ni fausse donnée.
 * Communication garde son hub métier historique — les cinq autres familles
 * partagent cette grammaire.
 */

/**
 * COMPTES RÉELS DE CERTAINES TUILES (Signalements, 09/10/2026).
 *
 * Une tuile reste une NAVIGATION : la synthèse n'y ajoute qu'un compte et une
 * phrase, lus à l'ouverture du hub auprès d'UNE route de synthèse, jamais
 * stockés. Chaque composant vérifie lui-même le droit de lecture avant
 * d'appeler, et dit honnêtement quand sa source est indisponible. Les autres
 * tuiles restent le registre, sans donnée.
 */
const SYNTHESES: Partial<Record<string, ComponentType>> = {
  signalements: SyntheseSignalements,
  // Exploitation (20/10/2026) : combien d'associations, combien à décider, la prochaine.
  associations: SyntheseAssociations,
  // Sécurité (22/10/2026) : les appels du jour à l'interphone IA, et rien d'autre.
  'interphone-ia': SyntheseInterphone,
}

function TuileModule({ c }: { c: NavEntry }) {
  const Synthese = c.state === 'AVAILABLE' ? SYNTHESES[c.id] : undefined
  const menuLien = useMenuLien()
  const corps = (
    <>
      <span className="lc-card__edge" aria-hidden="true" />
      <span className="lc-card__icon" aria-hidden="true"><c.icon /></span>
      <div className="fh-tile__texte">
        <h2>{c.label}</h2>
        {Synthese ? <Synthese /> : null}
        <p className="lc-hint">{c.description}</p>
        {c.provider && c.state !== 'AVAILABLE' ? (
          <span className="fh-tile__provider">Fournisseur pressenti : {c.provider}</span>
        ) : null}
      </div>
      <div className="fh-tile__pied">
        {c.state === 'NOT_CONFIGURED' ? <span className="lc-pill lc-pill--idle">{MODULE_STATE_LABELS.NOT_CONFIGURED}</span> : null}
        {c.state === 'PLANNED' ? <span className="lc-pill lc-pill--idle fh-tile__avenir">{MODULE_STATE_LABELS.PLANNED}</span> : null}
        {c.state === 'EXTERNAL' ? <span className="lc-pill lc-pill--idle">{MODULE_STATE_LABELS.EXTERNAL}</span> : null}
        {c.route ? (
          <span className="fh-tile__cta">
            {c.state === 'NOT_CONFIGURED' ? 'Configurer' : c.state === 'EXTERNAL' ? 'Ouvrir' : ''}
            {c.state === 'EXTERNAL' ? <ArrowUpRight size={15} aria-hidden="true" /> : <ChevronRight size={16} aria-hidden="true" />}
          </span>
        ) : null}
      </div>
    </>
  )
  // Une tuile À VENIR n'ouvre rien : pas de lien, pas de faux clic (§78).
  if (!c.route) return <div className="lc-card fh-tile fh-tile--muette lc-in">{corps}</div>
  return <Link href={c.route} className="lc-card fh-tile lc-in" {...menuLien(c.route, c.label)}>{corps}</Link>
}

export function FamilyHubPage({ familyId }: { familyId: string }) {
  const meta = useShellMeta()
  const fam = SITE_FAMILIES.find((f) => f.id === familyId) as NavFamily
  const permissions = usePermissions()
  const peut = (perm?: string) => !perm
    || !permissions.authenticated
    || permissions.permissions.includes(perm)
  // §122 : uniquement les tuiles accessibles — les futures (PLANNED) sont
  // globales et visibles, elles ne mènent nulle part.
  const tuiles = fam.children.filter((c) => c.state === 'PLANNED' || peut(c.permission))

  return (
    <Shell active={fam.id as ShellActive} meta={meta}>
      <main className="lc-page lc-stagger fh-page">
        <header className="fh-hero">
          <span className="fh-hero__icon" aria-hidden="true"><fam.icon size={26} /></span>
          <div>
            <h1 className="lc-title">{fam.label}</h1>
            <p className="lc-subtitle">{fam.description}</p>
          </div>
        </header>
        <div className="fh-grid">
          {tuiles.map((c) => <TuileModule key={c.id} c={c} />)}
        </div>
      </main>
    </Shell>
  )
}
